import { Component } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';
import { useState } from 'react';

/**
 * Error Boundary Component
 * Catches unhandled errors and displays a user-friendly error page.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Fallback UI Component
 */
function ErrorFallback({ error, errorInfo, onRetry }) {
  const [copied, setCopied] = useState(false);

  const errorDetails = {
    message: error?.message || 'Unknown error',
    stack: error?.stack?.slice(0, 500) || 'No stack trace',
    componentStack: errorInfo?.componentStack?.slice(0, 300) || 'No component stack',
    time: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent
  };

  const handleCopyError = async () => {
    const text = `
Error Report
============
Time: ${errorDetails.time}
URL: ${errorDetails.url}
Message: ${errorDetails.message}

Stack Trace:
${errorDetails.stack}

Component Stack:
${errorDetails.componentStack}

User Agent: ${errorDetails.userAgent}
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-card border-border">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <CardTitle className="text-xl font-barlow text-foreground">
            Something went wrong
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            An unexpected error occurred. Please try again or contact support if the problem persists.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="font-mono text-red-500 break-all">
              {errorDetails.message}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button onClick={onRetry} className="flex-1">
              <RefreshCw className="w-4 h-4 me-2" />
              Retry
            </Button>
            <Button variant="outline" onClick={handleCopyError}>
              {copied ? (
                <>
                  <Check className="w-4 h-4 me-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 me-2" />
                  Copy Error Details
                </>
              )}
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground">
            If this error continues, please share the copied error details with support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
